import { FailConfirmModal } from './FailConfirmModal';
import { AmrapConfirmModal } from './AmrapConfirmModal';
import type { AmrapPrompt } from './useAmrapConfirm';

// The prompts that fire ON COMPLETING A SET, in one place.
//
// They are a different family from LoggerModals: those are pickers and config
// sheets the athlete opens on purpose, driven by open/close pairs owned in
// useLoggerScreen. These two are raised BY the commit itself, own their state
// inside useTimelineLogger (useFailConfirm / useAmrapConfirm), and each one
// decides what gets written about the set that was just closed.
//
// Grouping them is not tidiness. Two uncoordinated <Modal>s on one gesture is a
// bug this codebase has already shipped once (useEffortCommit, 2026-07-06: the
// fail prompt and the effort-scale intro stacked). With both prompts rendered
// from one place, "can these two be open at once" is a question with a visible
// answer instead of a race between two useStates in two hooks.
//
// Today they cannot collide by construction: the fail prompt is raised only by
// onLogFail and the AMRAP prompt only by onLogSet, which are the two mutually
// exclusive legs of a completion. The explicit ordering below is there for the
// day that stops being true — it fails closed, showing one prompt, rather than
// stacking.

type Props = {
  failPrompt: { attempted: number } | null;
  onConfirmFail: (savedReps: number) => void;
  onCancelFail: () => void;
  amrapPrompt: AmrapPrompt;
  onConfirmAmrap: () => void;
  onDismissAmrap: () => void;
};

export function LoggerCommitPrompts({
  failPrompt,
  onConfirmFail,
  onCancelFail,
  amrapPrompt,
  onConfirmAmrap,
  onDismissAmrap,
}: Props) {
  // Fail first: it is the prompt that corrects the rep COUNT, so leaving it
  // behind an AMRAP question would let the athlete answer about a number that
  // is still wrong.
  if (failPrompt) {
    return (
      <FailConfirmModal
        attempted={failPrompt.attempted}
        onConfirm={onConfirmFail}
        onCancel={onCancelFail}
      />
    );
  }
  if (amrapPrompt) {
    return (
      <AmrapConfirmModal
        onConfirm={onConfirmAmrap}
        onDismiss={onDismissAmrap}
        reps={amrapPrompt.reps}
        {...(amrapPrompt.floor ? { floor: amrapPrompt.floor } : {})}
      />
    );
  }
  return null;
}
